import { useEffect, useRef, useState } from 'react';
import type { BugDetail, JiraTransition } from '@sr-test-wizard/shared';
import { useBugReviewStore } from '../../state/bugReviewStore';

interface Props {
  bug: BugDetail;
}

/**
 * Action bar pinned to the bottom of the Bugs detail pane. Lets the
 * tester:
 *   - Re-pull the source archive (overwrites `files/` for the run; useful
 *     when the bug claims a BE fix landed and we need to re-run analysis).
 *   - Upload a new image to the run's images dir (used when verifying a
 *     visual fix without going through Drive sync).
 *   - Post a comment, optionally drafted by the AI from the chat.
 *   - Transition the bug to a new status, optionally posting the same
 *     drafted comment alongside the transition.
 */
export function BugActionsBar({ bug }: Props) {
  const transitionsByKey = useBugReviewStore((s) => s.transitionsByKey);
  const loadTransitions = useBugReviewStore((s) => s.loadTransitions);
  const transition = useBugReviewStore((s) => s.transition);
  const draftComment = useBugReviewStore((s) => s.draftComment);
  const postComment = useBugReviewStore((s) => s.postComment);
  const repullSource = useBugReviewStore((s) => s.repullSource);
  const uploadImage = useBugReviewStore((s) => s.uploadImage);
  const postingComment = useBugReviewStore((s) => s.postingComment);

  const [intent, setIntent] = useState('');
  const [comment, setComment] = useState('');
  const [drafting, setDrafting] = useState(false);
  const [busyAction, setBusyAction] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const transitions = transitionsByKey[bug.key];

  useEffect(() => {
    if (transitions === undefined) {
      void loadTransitions(bug.key);
    }
  }, [bug.key, loadTransitions, transitions]);

  const flash = (msg: string) => {
    setInfo(msg);
    setTimeout(() => setInfo((v) => (v === msg ? null : v)), 4000);
  };

  const draft = async () => {
    setDrafting(true);
    setError(null);
    try {
      const body = await draftComment(bug.key, intent.trim() || undefined);
      setComment(body);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setDrafting(false);
    }
  };

  const post = async () => {
    if (!comment.trim()) return;
    setBusyAction('comment');
    setError(null);
    try {
      await postComment(bug.key, comment);
      flash('Comment posted to Jira.');
      setComment('');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusyAction(null);
    }
  };

  const transitionTo = async (t: JiraTransition) => {
    setBusyAction(`transition:${t.id}`);
    setError(null);
    try {
      await transition(bug.key, t.id, comment.trim() || undefined);
      flash(
        comment.trim()
          ? `Bug moved to "${t.toStatus}" with the comment above.`
          : `Bug moved to "${t.toStatus}".`,
      );
      setComment('');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusyAction(null);
    }
  };

  const repull = async () => {
    setBusyAction('repull');
    setError(null);
    try {
      const result = await repullSource(bug.key);
      flash(
        `Source re-pulled from ${result.orderKey} (${(result.bytes / 1024 / 1024).toFixed(1)} MB).`,
      );
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusyAction(null);
    }
  };

  const onFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusyAction('upload');
    setError(null);
    try {
      const result = await uploadImage(file);
      flash(`Uploaded ${result.name} (${(result.size / 1024).toFixed(0)} KB).`);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusyAction(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const repulling = busyAction === 'repull';
  const uploading = busyAction === 'upload';
  const transitioning = busyAction?.startsWith('transition:') ?? false;
  const posting = busyAction === 'comment' || postingComment;

  return (
    <div className="bug-actions">
      <div className="bug-actions-row">
        <button
          type="button"
          className="btn"
          disabled={repulling}
          onClick={() => void repull()}
          title="Re-download the source archive for this run from GitLab. Overwrites the existing files/ folder."
        >
          {repulling ? 'Re-pulling source...' : 'Re-pull source'}
        </button>
        <label className="btn" style={{ cursor: 'pointer' }}>
          {uploading ? 'Uploading...' : 'Upload image'}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            disabled={uploading}
            onChange={(e) => void onFileChange(e)}
            style={{ display: 'none' }}
          />
        </label>
        <span className="spacer" />
        <span className="muted small">
          Status: <strong>{bug.status ?? 'Unknown'}</strong>
        </span>
      </div>

      <div className="flex-col" style={{ marginTop: 8 }}>
        <label className="small muted">Comment intent (optional, used by the AI draft)</label>
        <input
          value={intent}
          onChange={(e) => setIntent(e.target.value)}
          placeholder='e.g. "we are accepting the fix", "rejecting; still missing X"'
        />
        <label className="small muted">Comment body (Markdown)</label>
        <textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Type a comment, or click 'Draft from chat' to let the AI write one based on the chat above."
          style={{ minHeight: 120 }}
        />
        <div className="bug-actions-row">
          <button type="button" className="btn" disabled={drafting} onClick={() => void draft()}>
            {drafting ? 'Drafting...' : 'Draft from chat'}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={posting || !comment.trim()}
            onClick={() => void post()}
          >
            {posting ? 'Posting...' : 'Post comment'}
          </button>
          <span className="spacer" />
          {(transitions ?? []).map((t) => (
            <button
              key={t.id}
              type="button"
              className="btn"
              disabled={transitioning}
              onClick={() => void transitionTo(t)}
              title={
                comment.trim()
                  ? `Move bug to "${t.toStatus}" and post the comment above.`
                  : `Move bug to "${t.toStatus}".`
              }
            >
              {busyAction === `transition:${t.id}` ? 'Moving...' : t.name}
            </button>
          ))}
        </div>
      </div>

      {info && (
        <p
          className="small"
          style={{
            marginTop: 8,
            padding: '6px 10px',
            borderRadius: 6,
            border: '1px solid rgba(60, 200, 120, 0.4)',
            background: 'rgba(60, 200, 120, 0.08)',
            color: 'var(--good)',
          }}
        >
          {info}
        </p>
      )}
      {error && (
        <p style={{ color: 'var(--bad)', marginTop: 8 }} className="small">
          {error}
        </p>
      )}
    </div>
  );
}
